import React, { useState, useCallback } from 'react';
import { supabase } from '../../../supabaseClient';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  useTheme,
  alpha,
  LinearProgress
} from '@mui/material';
import {
  Science as ScienceIcon,
  Assessment as AssessmentIcon,
  AttachMoney as AttachMoneyIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  Inventory as InventoryIcon
} from '@mui/icons-material';

const FormulationsData = ({ companyId }) => {
  const theme = useTheme();
  const [formulationData, setFormulationData] = useState({
    totalFormulations: 0,
    formulationsByStatus: [],
    topIngredients: [],
    costAnalysis: {
      averageCost: 0,
      totalValue: 0,
      costRange: { min: 0, max: 0 }
    },
    progressMetrics: {
      averageProgress: 0,
      completedFormulations: 0,
      inProgressFormulations: 0
    },
    testingMetrics: {
      totalTests: 0,
      passedTests: 0,
      failedTests: 0,
      passRate: 0
    },
    procedureMetrics: {
      totalSteps: 0,
      averageStepsPerFormulation: 0
    },
    recentActivity: []
  });

  const fetchFormulationData = useCallback(async () => {
    try {
      // Fetch main formulations data
      const { data: formulationsData, error: formulationsError } = await supabase
        .from('formulations')
        .select(`
          id,
          name,
          status,
          overall_progress_percentage,
          created_at,
          updated_at,
          approved_at
        `)
        .eq('company_id', companyId)
        .eq('is_active', true);

      if (formulationsError) throw formulationsError;

      // First, get formulation IDs for this company
      const formulationIds = formulationsData?.map(f => f.id) || [];

      // Fetch ingredients usage data
      const { data: ingredientsData, error: ingredientsError } = await supabase
        .from('formulation_ingredients_new')
        .select(`
          ingredient_id,
          percentage,
          amount,
          raw_materials (
            name,
            unit_cost,
            primary_unit_measure
          ),
          formulations!inner (
            company_id,
            name
          )
        `)
        .eq('formulations.company_id', companyId);

      if (ingredientsError) throw ingredientsError;

      // Fetch testing data
      const { data: testsData, error: testsError } = await supabase
        .from('formulation_tests')
        .select(`
          id,
          test_type,
          status,
          is_passed,
          test_date,
          formulations!inner (
            company_id,
            name
          )
        `)
        .eq('formulations.company_id', companyId);

      if (testsError) throw testsError;

      // Fetch procedure steps data
      const { data: proceduresData, error: proceduresError } = await supabase
        .from('procedure_steps')
        .select(`
          id,
          step_title,
          formulation_id,
          step_order,
          criticality_level
        `)
        .eq('company_id', companyId);

      if (proceduresError) throw proceduresError;

      // Process the data
      const totalFormulations = formulationsData?.length || 0;

      // Status breakdown
      const statusCounts = {};
      formulationsData?.forEach(f => {
        statusCounts[f.status] = (statusCounts[f.status] || 0) + 1;
      });

      const formulationsByStatus = Object.entries(statusCounts)
        .map(([status, count]) => ({ status, count }))
        .sort((a, b) => b.count - a.count);

      // Progress metrics
      const progressValues = formulationsData?.map(f => f.overall_progress_percentage || 0) || [];
      const averageProgress = progressValues.length > 0 
        ? progressValues.reduce((sum, val) => sum + val, 0) / progressValues.length 
        : 0;
      
      const completedFormulations = formulationsData?.filter(f => f.overall_progress_percentage === 100).length || 0;
      const inProgressFormulations = formulationsData?.filter(f => 
        f.overall_progress_percentage > 0 && f.overall_progress_percentage < 100
      ).length || 0;

      // Simple cost analysis - just set to zero for now
      const averageCost = 0;
      const totalValue = 0;
      const costRange = { min: 0, max: 0 };

      // Top ingredients analysis
      const ingredientUsage = {};
      ingredientsData?.forEach(ing => {
        const name = ing.raw_materials?.name || 'Unknown';
        if (!ingredientUsage[name]) {
          ingredientUsage[name] = {
            name,
            totalUsage: 0,
            formulationCount: 0,
            averagePercentage: 0,
            totalPercentage: 0
          };
        }
        ingredientUsage[name].totalUsage += ing.amount || 0;
        ingredientUsage[name].formulationCount += 1;
        ingredientUsage[name].totalPercentage += ing.percentage || 0;
      });

      const topIngredients = Object.values(ingredientUsage)
        .map(ing => ({
          ...ing,
          averagePercentage: ing.totalPercentage / ing.formulationCount
        }))
        .sort((a, b) => b.formulationCount - a.formulationCount)
        .slice(0, 5);

      // Testing metrics
      const totalTests = testsData?.length || 0;
      const passedTests = testsData?.filter(t => t.is_passed === true).length || 0;
      const failedTests = testsData?.filter(t => t.is_passed === false).length || 0;
      const passRate = totalTests > 0 ? (passedTests / totalTests) * 100 : 0;

      // Procedure metrics
      const totalSteps = proceduresData?.length || 0;
      const averageStepsPerFormulation = totalFormulations > 0 ? totalSteps / totalFormulations : 0;

      // Recent activity (last 10 formulations by update date)
      const recentActivity = formulationsData
        ?.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
        .slice(0, 5)
        .map(f => ({
          id: f.id,
          name: f.name,
          status: f.status,
          progress: f.overall_progress_percentage || 0,
          updated_at: f.updated_at
        })) || [];

      setFormulationData({
        totalFormulations,
        formulationsByStatus,
        topIngredients,
        costAnalysis: {
          averageCost,
          totalValue,
          costRange
        },
        progressMetrics: {
          averageProgress,
          completedFormulations,
          inProgressFormulations
        },
        testingMetrics: {
          totalTests,
          passedTests,
          failedTests,
          passRate
        },
        procedureMetrics: {
          totalSteps,
          averageStepsPerFormulation
        },
        recentActivity
      });

    } catch (err) {
      console.error('Error fetching formulation data:', err);
      // Initialize empty data on error
      setFormulationData({
        totalFormulations: 0,
        formulationsByStatus: [],
        topIngredients: [],
        costAnalysis: {
          averageCost: 0,
          totalValue: 0,
          costRange: { min: 0, max: 0 }
        },
        progressMetrics: {
          averageProgress: 0,
          completedFormulations: 0,
          inProgressFormulations: 0
        },
        testingMetrics: {
          totalTests: 0,
          passedTests: 0,
          failedTests: 0,
          passRate: 0
        },
        procedureMetrics: {
          totalSteps: 0,
          averageStepsPerFormulation: 0
        },
        recentActivity: []
      });
    }
  }, [companyId]);

  React.useEffect(() => {
    if (companyId) {
      fetchFormulationData();
    }
  }, [companyId, fetchFormulationData]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatNumber = (number) => {
    return new Intl.NumberFormat('en-US').format(Math.round(number));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved': return 'success';
      case 'in_review': return 'warning';
      case 'draft': return 'info';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status?.toLowerCase()) {
      case 'in_review': return 'In Review';
      case 'approved': return 'Approved';
      case 'draft': return 'Draft';
      case 'rejected': return 'Rejected';
      default: return status || 'Unknown';
    }
  };

  return (
    <Box sx={{ mb: 6 }}>
      {/* Key Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  mr: 2
                }}>
                  <ScienceIcon sx={{ fontSize: 24, color: 'primary.main' }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  Total Formulations
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ 
                fontWeight: 500, 
                color: 'primary.main',
                mb: 1
              }}>
                {formatNumber(formulationData.totalFormulations)}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                Active formulations
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  bgcolor: alpha(theme.palette.success.main, 0.1),
                  mr: 2
                }}>
                  <TrendingUpIcon sx={{ fontSize: 24, color: 'success.main' }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  Average Progress
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ 
                fontWeight: 500, 
                color: 'success.main',
                mb: 1
              }}>
                {formulationData.progressMetrics.averageProgress.toFixed(1)}%
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                Completion rate
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  bgcolor: alpha(theme.palette.info.main, 0.1),
                  mr: 2
                }}>
                  <CheckCircleIcon sx={{ fontSize: 24, color: 'info.main' }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  Completed
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ 
                fontWeight: 500, 
                color: 'info.main',
                mb: 1
              }}>
                {formatNumber(formulationData.progressMetrics.completedFormulations)}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                Finished formulations
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  bgcolor: alpha(theme.palette.warning.main, 0.1),
                  mr: 2
                }}>
                  <CheckCircleIcon sx={{ fontSize: 24, color: 'warning.main' }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  Test Pass Rate
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ 
                fontWeight: 500, 
                color: formulationData.testingMetrics.totalTests > 0 ? 'warning.main' : 'text.secondary',
                mb: 1
              }}>
                {formulationData.testingMetrics.totalTests > 0 
                  ? `${formulationData.testingMetrics.passRate.toFixed(1)}%`
                  : 'No data'
                }
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                Quality control
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Analytics Widgets Grid */}
      <Grid container spacing={4}>
        {/* Formulation Status Distribution */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
            }
          }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" sx={{ 
                fontWeight: 500, 
                color: 'text.primary',
                mb: 3
              }}>
                Status Distribution
              </Typography>
              {formulationData.formulationsByStatus.length > 0 ? (
                <Stack spacing={2}>
                  {formulationData.formulationsByStatus.map((item, index) => (
                    <Box key={item.status} sx={{ 
                      p: 2, 
                      borderRadius: 2, 
                      bgcolor: alpha(theme.palette.grey[100], 0.5),
                      border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                      }
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                          {getStatusLabel(item.status)}
                        </Typography>
                        <Chip 
                          label={item.count}
                          size="small"
                          color={getStatusColor(item.status)}
                          sx={{ fontWeight: 500 }}
                        />
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={(item.count / formulationData.totalFormulations) * 100}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: alpha(theme.palette.grey[300], 0.3),
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 4
                          }
                        }}
                      />
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Box sx={{ 
                  height: 200, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  color: 'text.secondary'
                }}>
                  <Typography variant="body1">No formulations found</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Top Ingredients */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
            }
          }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" sx={{ 
                fontWeight: 500, 
                color: 'text.primary',
                mb: 3
              }}>
                Most Used Ingredients
              </Typography>
              {formulationData.topIngredients.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500, color: 'text.primary' }}>Ingredient</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 500, color: 'text.primary' }}>Usage</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 500, color: 'text.primary' }}>Avg %</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {formulationData.topIngredients.map((ingredient, index) => (
                        <TableRow key={ingredient.name} sx={{
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.05)
                          }
                        }}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Chip 
                                label={index + 1}
                                size="small"
                                sx={{ 
                                  mr: 1,
                                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                                  color: 'primary.main',
                                  fontWeight: 500,
                                  minWidth: 24
                                }}
                              />
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {ingredient.name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 500 }}>
                            {ingredient.formulationCount} formulations
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 500, color: 'primary.main' }}>
                            {ingredient.averagePercentage.toFixed(2)}%
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ 
                  height: 200, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  color: 'text.secondary'
                }}>
                  <Typography variant="body1">No ingredient data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
            }
          }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" sx={{ 
                fontWeight: 500, 
                color: 'text.primary',
                mb: 3
              }}>
                Recent Formulation Activity
              </Typography>
              {formulationData.recentActivity.length > 0 ? (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500, color: 'text.primary' }}>Formulation</TableCell>
                        <TableCell sx={{ fontWeight: 500, color: 'text.primary' }}>Status</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 500, color: 'text.primary' }}>Progress</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 500, color: 'text.primary' }}>Last Updated</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {formulationData.recentActivity.map((activity) => (
                        <TableRow key={activity.id} sx={{
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.05)
                          }
                        }}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {activity.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={getStatusLabel(activity.status)}
                              size="small"
                              color={getStatusColor(activity.status)}
                              sx={{ fontWeight: 500 }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 40 }}>
                                {activity.progress}%
                              </Typography>
                              <LinearProgress 
                                variant="determinate" 
                                value={activity.progress}
                                sx={{
                                  width: 80,
                                  height: 6,
                                  borderRadius: 3,
                                  bgcolor: alpha(theme.palette.grey[300], 0.3),
                                  '& .MuiLinearProgress-bar': {
                                    borderRadius: 3
                                  }
                                }}
                              />
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                            {formatDate(activity.updated_at)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ 
                  height: 200, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  color: 'text.secondary'
                }}>
                  <Typography variant="body1">No recent activity</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FormulationsData;